import PropTypes from 'prop-types';
import React, {
  useEffect,
  useState,
  useMemo,
} from 'react';
import { useMutation } from '@apollo/client';

import { Map, List, Iterable } from 'immutable';
import isEmpty from 'lodash/isEmpty';

import Alert          from '@mui/material/Alert';
import Button         from '@mui/material/Button';
import LoadingButton  from '@mui/lab/LoadingButton';
import Grid           from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import Typography     from '@mui/material/Typography';

import ChevronLeft  from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';

import { CANDIDATE_APPLICATION_PROMPT_RESPONSE_INPUT } from 'queries/prompt/create';
import useBlobRecord from 'helpers/direct_upload/blob_record';
import debounce from 'helpers/debounce';
import { Display } from 'components';
import ApolloWrapper from 'pages/LandingPage/ApolloWrapper';
import NotSupportedModal from './NotSupportedModal';
import { clearItem } from 'helpers/local_storage';
import { isUserAgentAllowedForRecording } from 'helpers/hardware_access';
import PromptResponse from './PromptResponse';

const FILE_TYPES = ['audio', 'video', 'file_upload', 'picture'];
const RECORD_TYPES = ['audio', 'video'];

const MAX_ANSWERS_PER_REQUEST = 10; // API Max per request is 100 but 10 to make it faster (Long Form only, will autosave when 10 answers are in queue)

function Answers({ application, beDone, isLongForm, isAlwaysSubmittable, checkFollowups, refetched, allowLandingPageResponseResubmit, country, answerCountAutosave = MAX_ANSWERS_PER_REQUEST }) {
  const [state, setState] = useState(new Map());

  const stateApplication   = state.get('stateApplication', new Map(application));
  const questions          = state.get('questions', new List(stateApplication.get('responses').filter((response) => isEmpty(response.get('value') || response.get('docs') || response.get('url')))));
  const questionIndex      = state.get('questionIndex', 0);
  const responses          = state.get('responses', new List());
  const responsesToPersist = state.get('responsesToPersist', new Set());
  const progress           = state.get('progress', 0);
  const responsesAnswered  = state.get('responsesAnswered', 0);
  const isSubmittable      = state.get('isSubmittable', false);
  const forceSave          = state.get('forceSave', null);

  const fromSubmit         = state.get('fromSubmit', false);
  const pendingResponse    = state.get('pendingResponse', null); // Handle multiple answers?
  const blobsToUpload      = state.get('blobsToUpload', []);
  const uploadingBlobs     = state.get('uploadingBlobs', false);
  const error              = state.get('error', undefined);

  const unsupportedBrowser = state.get('unsupportedBrowser', false);

  const [submitResponses, returnResponse] = useMutation(CANDIDATE_APPLICATION_PROMPT_RESPONSE_INPUT);
  const [, directUpload, directUploadError] = useBlobRecord(blobsToUpload, 'response');

  const questiontypes = questions.filter((q) => q.getIn(['prompt', 'typeName']) === 'Question');

  const singleQuestionRequired = useMemo(() => {
    return questiontypes.some(
      (question) => {
        const timerEnabled = question.getIn(['prompt', 'metadata', 'parameters', 'timer_enabled']);
        const timer = question.getIn(['prompt', 'metadata', 'parameters', 'timer']);

        return timerEnabled && timer;
      }
    );
  }, [questiontypes]);

  useEffect(() => {
    if (!isUserAgentAllowedForRecording(navigator.userAgent)) {
      const recordingQuestion = questiontypes.some((question) => RECORD_TYPES.includes(question.getIn(['prompt', 'categoryName'])));

      setState(state.set('unsupportedBrowser', recordingQuestion));
      return;
    }
  }, [questiontypes]);

  const singleQuestionMode = useMemo(() => {
    return singleQuestionRequired || !isLongForm;
  }, [singleQuestionRequired, isLongForm]);

  const showBackButton = useMemo(() => {
    return singleQuestionRequired ? false : allowLandingPageResponseResubmit;
  }, [singleQuestionRequired, allowLandingPageResponseResubmit]);

  useEffect(() => {
    if (returnResponse.data === undefined) return;

    if (fromSubmit) {
      checkFollowups(); // fromSubmit prevents that the last answer autosaves and trigger checkFollowups()
    }
  }, [returnResponse]);

  useEffect(() => {
    if (!forceSave) return;

    debounce(() => {
      if (questionIndex + 1 >= questiontypes.size) {
        onSubmit();
      } else {
        setState(state.set('questionIndex', questionIndex + 1));
      }
    }, 100, 'submit');
  }, [forceSave]);

  useEffect(() => {
    if (refetched) {
      setQuestionIndex(0);
    }
  }, [refetched]);

  useEffect(() => {
    // below is the computation for progress (percentage) by
    // determining the current question the candidate is seeing
    // setState( state.set('progress', Math.round((questionIndex/questions.size) * 100)) );

    if (singleQuestionRequired || (!isLongForm && !isAlwaysSubmittable)) {
      save();
    }
  }, [questionIndex]);

  useEffect(() => {
    if (directUploadError === undefined) return;

    setState(state.set('error', t('eos.landing_page.text.upload_failed')).set('uploadingBlobs', false));
  }, [directUploadError]);

  useEffect(() => {
    if (isEmpty(directUpload)) return;

    let response = new Map(pendingResponse);

    if (response.get('blob')) {
      response = response.set('file', directUpload[0].signedBlobId);
    }

    addResponse(response.get('promptId'), response);
  }, [directUpload]);

  useEffect(() => {
    if (isEmpty(pendingResponse)) return;

    // Case where the user deleted a blob and then added a new file in the file upload is not yet considered
    const blobs = pendingResponse.get('blob');
    setState(state.set('blobsToUpload', blobs));
  }, [pendingResponse]);

  useEffect(() => {
    let submittable  = false;
    // isolate file questions. we will check if there is a blob file uploaded

    const fileQuestions   = questiontypes.filter((q) => FILE_TYPES.includes(q.getIn(['prompt', 'categoryName']))).map((q) => q.getIn(['prompt', 'id']));
    // isolate text questions
    const normalQuestions = questiontypes.filter((q) => !FILE_TYPES.includes(q.getIn(['prompt', 'categoryName']))).map((q) => q.getIn(['prompt', 'id']));

    // get all text responses which are answered
    const answeredNormals = responses.filter((res) => res.get('value') !== '' && normalQuestions.includes(res.get('promptId')));

    // get all file responses which are answered
    const answeredFiles   = responses.filter((res) =>
      (
        (
          (res.get('file') && res.get('file') !== '') ||
          (res.get('blobs') && res.get('blobs').length > 0 && res.get('blobs').every((blob) => blob.get('signedBlobId'))) ||
          res.get('value') !== ''
        ) &&
        fileQuestions.includes(res.get('promptId'))
      )
    );

    if (responses.size === questiontypes.size) {
      // if answered is equal to normal questions
      if ((answeredNormals.size + answeredFiles.size) === questiontypes.size) {
        submittable = true;
      }
    }

    setState(state.set('progress', Math.round(((answeredNormals.size + answeredFiles.size) / questiontypes.size) * 100))
      .set('isSubmittable', submittable)
      .set('responsesAnswered', (answeredNormals.size + answeredFiles.size)));
  }, [responses, forceSave]);

  useEffect(() => {
    if (forceSave || isSubmittable) return;

    if (responsesToPersist.size >= answerCountAutosave) save();
  }, [responses, responsesToPersist, forceSave, isSubmittable]);

  function setQuestionIndex(index) {
    if (index < 0 || index === questiontypes.size) return;
    setState(state.set('questionIndex', index).set('forceSave', null));
  }

  function setBlobName(blobs) {
    if (isEmpty(blobs)) return blobs;

    blobs.forEach((blob) => {
      const extension = blob.type.split('/').pop();
      blob.name = blob.name ? blob.name : `app${stateApplication.get('id')}-${Date.now()}.${extension}`;
    });

    return blobs;
  }

  function onChange(promptId, value) {
    const text   = value.get('text');
    const number = value.get('number');
    const blob   = setBlobName(value.get('blob'));
    const blobs  = value.get('blobs');
    const duration = value.get('duration');
    const incomplete = value.get('incomplete');
    const forced = value.get('forced');

    const geolocation = value.get('geolocation');

    const checks = [text, number, blob, blobs, geolocation];
    if (checks.every((check) => isEmpty(check))) {
      if (forced) {
        setState(state.set('forceSave', responses.find((res) => res.get('promptId') === promptId)));
        return;
      }

      removeResponse(promptId);
      return;
    }

    const question = questions.find((question) => question.getIn(['prompt', 'id']) === promptId);
    const questionType = question.getIn(['prompt', 'categoryName']);

    const response = new Map({
      applicationId: stateApplication.get('id'),
      value:         text || number,
      promptId,
      blob,
      blobs,
      geolocation,
      duration,
      incomplete,
      forced,
    });

    const toUploadFile = ['audio/text', 'audio', 'video', 'picture'].includes(questionType) && blob;

    if (toUploadFile) {
      setState(state.set('pendingResponse', response)
        .set('uploadingBlobs', true));
      return;
    }

    // Case when deleting a file in a file_upload input will not go through the above condition.
    addResponse(promptId, response);
  }

  function removeResponse(promptId) {
    const pidx = responses.findIndex((res) => res.get('promptId') === promptId);

    if (pidx >= 0) setState(state.set('responses', responses.delete(pidx)));
  }

  function addResponse(promptId, response) {
    const pidx = responses.findIndex((res) => res.get('promptId') === promptId);

    if (pidx === -1) {
      setState(state.set('responses', responses.push(response))
        .set('pendingResponse', null)
        .set('uploadingBlobs', false)
        .set('error', undefined)
        .set('responsesToPersist', !response.get('forced') ? responsesToPersist.add(promptId) : new Set())
        .set('forceSave', response.get('forced') ? response : null));

      return;
    }

    setState(state.setIn(['responses', pidx], response)
      .set('pendingResponse', null)
      .set('uploadingBlobs', false)
      .set('error', undefined)
      .set('responsesToPersist', !response.get('forced') ? responsesToPersist.add(promptId) : new Set())
      .set('forceSave', response.get('forced') ? response : null));
  }

  function onSubmit() {
    if (responsesToPersist.size > 0 || forceSave) {
      postResponses(getResponsesToPost(), true);

      questiontypes.forEach(
        (question) => clearItem(`${stateApplication.get('id')}-${question.getIn(['prompt', 'id'])}`)
      );

      // TODO: show the loading screen
      setState(state.set('fromSubmit', true)
        .set('responsesToPersist', new Set())
        .set('forceSave', null));
    } else {
      beDone();
    }
  }

  function save() {
    if ((responsesToPersist.size > 0 || forceSave) && !isSubmittable) postResponses(getResponsesToPost());

    setState(state.set('responsesToPersist', new Set()).set('forceSave', null));
  }

  // TODO: named params
  function postResponses(responses, fromsubmit = false) {
    if (!responses || responses.size === 0) return;

    submitResponses({
      variables:
      {
        response: responses,
      },
    });
  }

  function getResponsesToPost() {
    if (forceSave) return [parseResponse(forceSave).toJS()];

    return responses.filter((response) => !isEmpty(response) && responsesToPersist.has(response.get('promptId')))
      .map((response) => parseResponse(response)).toJS();
  }

  const parseResponse = (response) => {
    // Signed blobs id are stored in the blobs property
    // We need to populate the docs field with only the signed blobs id
    let parsedResponse = new Map(response);

    if (parsedResponse.get('blobs')) {
      parsedResponse = parsedResponse.set('docs', parsedResponse.get('blobs').map((blob) => ({ file: blob.get('signedBlobId'), recognition: blob.get('recognition') })));
    }

    // Need to remove the blobs and blob property
    return parsedResponse.delete('blobs').delete('blob').delete('duration').delete('forced');
  };

  function retry() {
    setState(state.set('blobsToUpload', [...blobsToUpload]));
  }

  return (
    <ApolloWrapper token={application.bearerToken}>
      {
        questiontypes.map((question, index) => {
          const response = responses.filter((res) => res.get('promptId') === question.getIn(['prompt', 'id'])).get(0);

          if (singleQuestionMode && questionIndex !== index) return null;

          return (<PromptResponse key={question.getIn(['prompt', 'id'])} applicationId={stateApplication.get('id')} question={question} response={response} onChange={onChange} index={index} disabled={(fromSubmit && returnResponse.loading) || uploadingBlobs} country={country} />)
        })
      }

      <Display if={singleQuestionMode}>
        <Grid container item xs={12} spacing={1} sx={{ mt: 5 }}>
          <Grid item md={10} xs={8}>
            <Typography variant="body2" color="text.secondary">{t('eos.landing_page.text.question_progress', { current: responsesAnswered, size: questiontypes.size })}</Typography>
          </Grid>
          <Grid item xs={12}>
            <LinearProgress value={progress} color="primary" variant="determinate" sx={{ height: 10 }} />
          </Grid>
        </Grid>
        <Display if={questiontypes.size > 1}>
          <Grid container item xs={12} spacing={1} sx={{ mt: 2 }}>
            <Grid item xs={6}>
              <Display if={showBackButton}>
                <Button disabled={questionIndex === 0 || returnResponse.loading || uploadingBlobs} variant="contained" color="primary" size="small" onClick={() => setQuestionIndex(questionIndex - 1)} sx={{ mr: 1 }} data-testid="prev"><ChevronLeft fontSize="small" /></Button>
              </Display>
            </Grid>
            <Grid item xs={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button disabled={(!isAlwaysSubmittable && questionIndex >= responsesAnswered) || questionIndex === (questiontypes.size - 1) || returnResponse.loading || uploadingBlobs} variant="contained" color="primary" size="small" onClick={() => setQuestionIndex(questionIndex + 1)} data-testid="next"><ChevronRight fontSize="small" /></Button>
            </Grid>
          </Grid>
        </Display>
      </Display>

      <Display if={error}>
        <Grid item xs={12} sx={{ display: 'flex', mt: 4, justifyContent: 'center' }}>
          <Alert severity="error">{error}</Alert>
          <Button variant="contained" color="primary" size="small" onClick={retry} data-testid="answers-index-retry-button">{t('eos.landing_page.label.retry')}</Button>
        </Grid>
      </Display>

      <Grid item xs={12} sx={{ display: 'flex', mt: 4, justifyContent: 'center' }}>
        <Display id="error" if={returnResponse.error && fromSubmit}>
          <Alert severity="error" sx={{ mr: 3 }}>{t('eos.landing_page.text.application_invalid')}</Alert>
        </Display>
        {/* TODO: display only if last question */}
        <LoadingButton disabled={!isSubmittable && !isAlwaysSubmittable} loading={returnResponse.loading || uploadingBlobs} disableElevation variant="contained" color="primary" size="large" onClick={() => onSubmit()}>{t('eos.landing_page.label.submit')}</LoadingButton>
      </Grid>

      <NotSupportedModal open={unsupportedBrowser} />
    </ApolloWrapper>
  );
}

Answers.defaultProps = {
  application: null,
  beDone:      null,
};

Answers.propTypes = {
  application: PropTypes.objectOf(Iterable),
  beDone:      PropTypes.func,
};

export default Answers;
